import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';

const API_URL = 'https://iia-backend-production.up.railway.app';

const CampusSquare = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [currentPath, setCurrentPath] = useState('--');
const [pathCompletion, setPathCompletion] = useState('--');

useEffect(() => {
  fetchStats();
}, []);

const fetchStats = async () => {
  try {
    const res = await axios.get(`${API_URL}/api/instructional/paths`);
    const paths = res.data.paths || [];
    if (paths.length > 0) {
      setCurrentPath(paths[0].title);

      // Calculate completion: completed tasks / total tasks across all courses in path
      try {
        const progressRes = await axios.get(
          `${API_URL}/api/instructional/paths/${paths[0].id}/progress`
        );
        if (progressRes.data.completion !== undefined) {
          setPathCompletion(`${progressRes.data.completion}%`);
        }
      } catch (err) {
        setPathCompletion('0%');
      }
    }
  } catch (err) {
    console.error(err);
  }
};

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const buildings = [
    { name: 'Instructional Center', icon: '🎓', path: '/instructional' },
    { name: 'Financial Office', icon: '💰', path: '/financial' },
    { name: 'Academic Resource Center', icon: '🏫', path: '/academic' },
    { name: 'Admissions Office', icon: '📋', path: '/admissions' },
    { name: 'Faculty Hall', icon: '🏛️', path: '/faculty', staffOnly: true },
    { name: 'Lounge', icon: '🛋️', path: '/lounge' },
  ];

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h1 style={styles.logo}>IIA</h1>
          <span style={styles.logoText}>Impact Influence Academy</span>
        </div>
        <div style={styles.headerRight}>
          <span style={styles.welcome}>Welcome, {user?.first_name}</span>
          {user?.role_id === 1 && (
            <button style={styles.adminBtn} onClick={() => navigate('/admin')}>
              🛠 Admin Panel
            </button>
          )}
          <button style={styles.logoutBtn} onClick={handleLogout}>
            Exit Campus
          </button>
        </div>
      </div>

      <div style={styles.main}>
        {/* Title */}
        <div style={styles.pageHeader}>
          <h2 style={styles.pageTitle}>🏛️ Campus Square</h2>
          <p style={styles.pageSubtitle}>Your campus hub</p>
        </div>

        {/* Buildings Grid */}
        <div style={styles.buildingsGrid}>
          {buildings.map(building => (
            <div
              key={building.name}
              style={styles.buildingCard}
              onClick={() => {
                    if (building.staffOnly && user?.role_id === 4) {
                      alert('Faculty Hall is for staff only.');
                      return;
                    }
                    navigate(building.path);
                  }}
            >
              <span style={styles.buildingIcon}>{building.icon}</span>
              <span style={styles.buildingName}>{building.name}</span>
            </div>
          ))}
        </div>

        {/* Stats Bar */}
        <div style={styles.statsBar}>
          <div style={styles.statItem}>
            <span style={styles.statLabel}>STUDENT</span>
            <span style={styles.statValue}>
              {user?.first_name} {user?.last_name}
            </span>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.statItem}>
            <span style={styles.statLabel}>CURRENT PATH</span>
            <span style={styles.statValue}>{currentPath}</span>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.statItem}>
            <span style={styles.statLabel}>PATH COMPLETION</span>
            <span style={styles.statValue}>{pathCompletion}</span>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.statItem}>
            <span style={styles.statLabel}>OVERALL GPA</span>
            <span style={styles.statValue}>--</span>
          </div>
          <div style={styles.statDivider} />
          <div style={styles.statItem}>
            <span style={styles.statLabel}>TODAY</span>
            <span style={styles.statValue}>
              {new Date().toLocaleDateString('en-US', { 
                month: 'long', day: 'numeric', year: 'numeric' 
              })}
            </span>
          </div>
        </div>

        {/* Boards */}
        <div style={styles.boardsGrid}>
          <UpdatesBoard user={user} />
          <CommunityBoard user={user} />
        </div>
      </div>
    </div>
  );
};

// ==================== UPDATES BOARD ====================
const UpdatesBoard = ({ user }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ title: '', body: '' });
  const [selectedReads, setSelectedReads] = useState(null);
  const isAdmin = user?.role_id <= 3;

  useEffect(() => { fetchAnnouncements(); }, []);

  const fetchAnnouncements = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/campus/announcements`);
      setAnnouncements(res.data.announcements);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreate = async () => {
    if (!form.title || !form.body) return;
    try {
      await axios.post(`${API_URL}/api/campus/announcements`, form);
      setForm({ title: '', body: '' });
      setShowForm(false);
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this announcement?')) return;
    try {
      await axios.delete(`${API_URL}/api/campus/announcements/${id}`);
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await axios.post(`${API_URL}/api/campus/announcements/${id}/read`);
      fetchAnnouncements();
    } catch (err) {
      console.error(err);
    }
  };

  const handleViewReads = async (id) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/campus/announcements/${id}/reads`
      );
      setSelectedReads(res.data.reads);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div style={styles.boardCard}>
      <div style={styles.boardHeader}>
        <div style={styles.boardHeaderLeft}>
          <h3 style={styles.boardTitle}>📢 Campus Updates</h3>
          <span style={styles.boardBadge}>Official</span>
        </div>
        {isAdmin && (
          <button style={styles.addBtn} onClick={() => setShowForm(!showForm)}>
            + Post
          </button>
        )}
      </div>

      {showForm && (
        <div style={styles.postForm}>
          <input
            style={styles.input}
            placeholder="Announcement title"
            value={form.title}
            onChange={e => setForm({...form, title: e.target.value})}
          />
          <textarea
            style={styles.textarea}
            placeholder="Announcement body"
            value={form.body}
            onChange={e => setForm({...form, body: e.target.value})}
            rows={3}
          />
          <div style={styles.formActions}>
            <button style={styles.saveBtn} onClick={handleCreate}>Post</button>
            <button style={styles.cancelBtn} onClick={() => setShowForm(false)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      <div style={styles.boardFeed}>
        {announcements.length === 0 && (
          <p style={styles.emptyText}>No updates yet</p>
        )}
        {announcements.map(ann => (
          <div key={ann.id} style={styles.annCard}>
            <div style={styles.annTop}>
              <h4 style={styles.annTitle}>{ann.title}</h4>
              <div style={styles.annActions}>
                {!ann.read_by_me ? (
                  <button style={styles.readBtn} onClick={() => handleMarkRead(ann.id)}>
                    Mark Read
                  </button>
                ) : (
                  <span style={styles.readBadge}>✓ Read</span>
                )}
                {isAdmin && (
                  <>
                    <button
                      style={styles.readsBtn}
                      onClick={() => handleViewReads(ann.id)}
                    >
                      {ann.read_count} reads
                    </button>
                    <button
                      style={styles.deleteSmallBtn}
                      onClick={() => handleDelete(ann.id)}
                    >
                      ×
                    </button>
                  </>
                )}
              </div>
            </div>
            <p style={styles.annBody}>{ann.body}</p>
            <p style={styles.annMeta}>
              {ann.first_name} {ann.last_name} · {new Date(ann.created_at).toLocaleDateString()}
            </p>
          </div>
        ))}
      </div>

      {selectedReads && (
        <div style={styles.modalOverlay} onClick={() => setSelectedReads(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Read by</h3>
            {selectedReads.length === 0 && (
              <p style={styles.emptyText}>No one has read this yet.</p>
            )}
            {selectedReads.map(r => (
              <div key={r.id} style={styles.readRow}>
                <span style={styles.readName}>
                  {r.first_name} {r.last_name}
                </span>
                <span style={styles.readTime}>
                  {new Date(r.read_at).toLocaleDateString()}
                </span>
              </div>
            ))}
            <button style={styles.closeBtn} onClick={() => setSelectedReads(null)}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== COMMUNITY BOARD ====================
const CommunityBoard = ({ user }) => {
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState('');
  const [expandedPost, setExpandedPost] = useState(null);
  const [comments, setComments] = useState({});
  const [newComment, setNewComment] = useState('');

  useEffect(() => { fetchPosts(); }, []);

  const fetchPosts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/campus/posts`);
      setPosts(res.data.posts);
    } catch (err) {
      console.error(err);
    }
  };

  const handlePost = async () => {
    if (!newPost.trim()) return;
    try {
      await axios.post(`${API_URL}/api/campus/posts`, { body: newPost });
      setNewPost('');
      fetchPosts();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await axios.delete(`${API_URL}/api/campus/posts/${postId}`);
      fetchPosts();
    } catch (err) {
      console.error(err);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const res = await axios.get(
        `${API_URL}/api/campus/posts/${postId}/comments`
      );
      setComments(prev => ({ ...prev, [postId]: res.data.comments }));
    } catch (err) {
      console.error(err);
    }
  };

  const handleExpandPost = (postId) => {
    if (expandedPost === postId) {
      setExpandedPost(null);
    } else {
      setExpandedPost(postId);
      fetchComments(postId);
    }
  };

  const handleComment = async (postId) => {
    if (!newComment.trim()) return;
    try {
      await axios.post(`${API_URL}/api/campus/posts/${postId}/comments`, {
        body: newComment
      });
      setNewComment('');
      fetchComments(postId);
      fetchPosts();
    } catch (err) {
      console.error(err);
    }
  };

  const handleDeleteComment = async (commentId, postId) => {
    if (!window.confirm('Delete this comment?')) return;
    try {
      await axios.delete(`${API_URL}/api/campus/comments/${commentId}`);
      fetchComments(postId);
      fetchPosts();
    } catch (err) {
      console.error(err);
    }
  };

  const timeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  };

  return (
    <div style={styles.boardCard}>
      <div style={styles.boardHeader}>
        <div style={styles.boardHeaderLeft}>
          <h3 style={styles.boardTitle}>💬 Community</h3>
          <span style={styles.boardBadgeOpen}>Open</span>
        </div>
      </div>

      <div style={styles.newPostBox}>
        <textarea
          style={styles.newPostInput}
          placeholder="Share something with the community..."
          value={newPost}
          onChange={e => setNewPost(e.target.value)}
          rows={2}
        />
        <button
          style={newPost.trim() ? styles.postBtn : styles.postBtnDisabled}
          onClick={handlePost}
          disabled={!newPost.trim()}
        >
          Post
        </button>
      </div>

      <div style={styles.boardFeed}>
        {posts.length === 0 && (
          <p style={styles.emptyText}>Be the first to post</p>
        )}
        {posts.map(post => (
          <div key={post.id} style={styles.postCard}>
            <div style={styles.postHeader}>
              <div style={styles.postAvatar}>
                {post.first_name?.[0]}{post.last_name?.[0]}
              </div>
              <div style={styles.postMeta}>
                <p style={styles.postAuthor}>
                  {post.first_name} {post.last_name}
                </p>
                <p style={styles.postTime}>{timeAgo(post.created_at)}</p>
              </div>
              {(user?.role_id === 1 || post.user_id === user?.id) && (
                <button
                  style={styles.deleteSmallBtn}
                  onClick={() => handleDeletePost(post.id)}
                >
                  ×
                </button>
              )}
            </div>
            <p style={styles.postBody}>{post.body}</p>
            <button
              style={styles.commentToggleBtn}
              onClick={() => handleExpandPost(post.id)}
            >
              💬 {post.comment_count} {Number(post.comment_count) === 1 ? 
                'comment' : 'comments'} {expandedPost === post.id ? '▲' : '▼'}
            </button>

            {expandedPost === post.id && (
              <div style={styles.commentsSection}>
                {(comments[post.id] || []).map(comment => (
                  <div key={comment.id} style={styles.commentRow}>
                    <div style={styles.commentAvatar}>
                      {comment.first_name?.[0]}{comment.last_name?.[0]}
                    </div>
                    <div style={styles.commentContent}>
                      <p style={styles.commentAuthor}>
                        {comment.first_name} {comment.last_name}
                        <span style={styles.commentTime}>
                          {' · '}{timeAgo(comment.created_at)}
                        </span>
                      </p>
                      <p style={styles.commentBody}>{comment.body}</p>
                    </div>
                    {(user?.role_id === 1 || comment.user_id === user?.id) && (
                      <button
                        style={styles.deleteCommentBtn}
                        onClick={() => handleDeleteComment(comment.id, post.id)}
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <div style={styles.newCommentBox}>
                  <input
                    style={styles.commentInput}
                    placeholder="Write a comment..."
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleComment(post.id)}
                  />
                  <button
                    style={styles.commentBtn}
                    onClick={() => handleComment(post.id)}
                  >
                    Reply
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#1A2744', color: '#ffffff' },
  header: { backgroundColor: '#0f1a33', borderBottom: '1px solid #C49A28', padding: '16px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  logo: { color: '#C49A28', fontSize: '24px', fontWeight: '800', margin: '0', letterSpacing: '2px' },
  logoText: { color: '#ffffff80', fontSize: '14px', letterSpacing: '1px' },
  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  welcome: { color: '#ffffff', fontSize: '14px' },
  adminBtn: { backgroundColor: '#1A6B8A', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' },
  logoutBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff30', color: '#ffffff60', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '13px' },
  main: { maxWidth: '1200px', margin: '0 auto', padding: '32px' },
  pageHeader: { marginBottom: '24px' },
  pageTitle: { color: '#C49A28', fontSize: '28px', fontWeight: '700', margin: '0 0 4px 0' },
  pageSubtitle: { color: '#ffffff60', fontSize: '14px', margin: '0' },
  buildingsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' },
  buildingCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff10', borderRadius: '12px', padding: '28px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px', cursor: 'pointer', textAlign: 'center' },
  buildingIcon: { fontSize: '32px' },
  buildingName: { color: '#ffffff', fontSize: '13px', fontWeight: '600' },
  statsBar: { backgroundColor: '#0f1a33', border: '1px solid #C49A2830', borderRadius: '10px', padding: '16px 28px', display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '24px' },
  statItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  statLabel: { color: '#ffffff40', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '1px' },
  statValue: { color: '#C49A28', fontSize: '14px', fontWeight: '700' },
  statDivider: { width: '1px', height: '32px', backgroundColor: '#ffffff15' },
  boardsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  boardCard: { backgroundColor: '#0f1a33', border: '1px solid #ffffff10', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' },
  boardHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #ffffff08' },
  boardHeaderLeft: { display: 'flex', alignItems: 'center', gap: '10px' },
  boardTitle: { color: '#ffffff', fontSize: '15px', fontWeight: '700', margin: '0' },
  boardBadge: { backgroundColor: '#C49A2820', color: '#C49A28', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  boardBadgeOpen: { backgroundColor: '#1A6B8A20', color: '#1A6B8A', fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  boardFeed: { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '500px', overflowY: 'auto' },
  addBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  postForm: { padding: '12px 16px', borderBottom: '1px solid #ffffff08', display: 'flex', flexDirection: 'column', gap: '8px' },
  input: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '8px 12px', color: '#ffffff', fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box' },
  textarea: { backgroundColor: '#1A2744', border: '1px solid #ffffff20', borderRadius: '6px', padding: '8px 12px', color: '#ffffff', fontSize: '13px', outline: 'none', resize: 'none', width: '100%', boxSizing: 'border-box' },
  formActions: { display: 'flex', gap: '8px' },
  saveBtn: { backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '6px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff20', color: '#ffffff60', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', cursor: 'pointer' },
  annCard: { backgroundColor: '#1A2744', borderRadius: '8px', padding: '14px' },
  annTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px' },
  annTitle: { color: '#C49A28', fontSize: '13px', fontWeight: '700', margin: '0' },
  annActions: { display: 'flex', gap: '6px', alignItems: 'center', flexShrink: 0 },
  annBody: { color: '#ffffffcc', fontSize: '13px', lineHeight: '1.6', margin: '0 0 6px 0' },
  annMeta: { color: '#ffffff40', fontSize: '11px', margin: '0' },
  readBtn: { backgroundColor: '#1A6B8A', color: '#ffffff', border: 'none', borderRadius: '4px', padding: '2px 8px', fontSize: '10px', fontWeight: '600', cursor: 'pointer' },
  readBadge: { color: '#1E8449', fontSize: '11px', fontWeight: '600' },
  readsBtn: { backgroundColor: 'transparent', border: '1px solid #ffffff15', color: '#ffffff50', borderRadius: '4px', padding: '2px 8px', fontSize: '10px', cursor: 'pointer' },
  deleteSmallBtn: { backgroundColor: 'transparent', border: 'none', color: '#ff666660', fontSize: '16px', cursor: 'pointer', padding: '0 2px' },
  emptyText: { color: '#ffffff30', fontSize: '13px', textAlign: 'center', padding: '20px 0', margin: '0' },
  newPostBox: { padding: '12px 16px', borderBottom: '1px solid #ffffff08', display: 'flex', flexDirection: 'column', gap: '8px' },
  newPostInput: { backgroundColor: '#1A2744', border: '1px solid #ffffff15', borderRadius: '6px', padding: '8px 12px', color: '#ffffff', fontSize: '13px', outline: 'none', resize: 'none', width: '100%', boxSizing: 'border-box' },
  postBtn: { alignSelf: 'flex-end', backgroundColor: '#C49A28', color: '#1A2744', border: 'none', borderRadius: '6px', padding: '6px 16px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' },
  postBtnDisabled: { alignSelf: 'flex-end', backgroundColor: '#C49A2840', color: '#1A274460', border: 'none', borderRadius: '6px', padding: '6px 16px', fontSize: '12px', fontWeight: '700', cursor: 'not-allowed' },
  postCard: { backgroundColor: '#1A2744', borderRadius: '8px', padding: '12px' },
  postHeader: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' },
  postAvatar: { width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#1A6B8A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: '11px', fontWeight: '700', flexShrink: 0 },
  postMeta: { flex: 1 },
  postAuthor: { color: '#ffffff', fontSize: '12px', fontWeight: '600', margin: '0' },
  postTime: { color: '#ffffff40', fontSize: '11px', margin: '0' },
  postBody: { color: '#ffffffcc', fontSize: '13px', lineHeight: '1.6', margin: '0 0 8px 0' },
  commentToggleBtn: { backgroundColor: 'transparent', border: 'none', color: '#ffffff50', fontSize: '11px', cursor: 'pointer', padding: '0' },
  commentsSection: { marginTop: '10px', paddingTop: '10px', borderTop: '1px solid #ffffff08', display: 'flex', flexDirection: 'column', gap: '8px' },
  commentRow: { display: 'flex', gap: '6px', alignItems: 'flex-start' },
  commentAvatar: { width: '24px', height: '24px', borderRadius: '50%', backgroundColor: '#1A2744', border: '1px solid #ffffff15', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: '10px', fontWeight: '700', flexShrink: 0 },
  commentContent: { flex: 1, backgroundColor: '#0f1a33', borderRadius: '6px', padding: '6px 10px' },
  commentAuthor: { color: '#ffffff', fontSize: '11px', fontWeight: '600', margin: '0 0 2px 0' },
  commentTime: { color: '#ffffff40', fontWeight: '400' },
  commentBody: { color: '#ffffffcc', fontSize: '12px', margin: '0' },
  deleteCommentBtn: { backgroundColor: 'transparent', border: 'none', color: '#ffffff20', fontSize: '14px', cursor: 'pointer', padding: '0' },
  newCommentBox: { display: 'flex', gap: '6px' },
  commentInput: { flex: 1, backgroundColor: '#0f1a33', border: '1px solid #ffffff10', borderRadius: '6px', padding: '6px 10px', color: '#ffffff', fontSize: '12px', outline: 'none' },
  commentBtn: { backgroundColor: '#1A6B8A', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '6px 12px', fontSize: '11px', fontWeight: '600', cursor: 'pointer' },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: '#00000080', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { backgroundColor: '#1A2744', border: '1px solid #C49A28', borderRadius: '12px', padding: '28px', maxWidth: '400px', width: '90%', display: 'flex', flexDirection: 'column', gap: '12px' },
  modalTitle: { color: '#C49A28', fontSize: '18px', fontWeight: '700', margin: '0' },
  readRow: { display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #ffffff10' },
  readName: { color: '#ffffff', fontSize: '13px' },
  readTime: { color: '#ffffff40', fontSize: '12px' },
  closeBtn: { backgroundColor: '#1A6B8A', color: '#ffffff', border: 'none', borderRadius: '6px', padding: '10px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' },
};

export default CampusSquare;